/**
 * Unwrap `{ success, result }` responses from organization merchant APIs.
 */
import { getStoreApiErrorMessage } from "../helper/storeApiErrorMessage"

export function unwrapOrganizationResult(response) {
  const body = response?.data
  if (body && typeof body === "object" && body.success === true && body.result != null) {
    return body.result
  }
  return body?.result ?? body
}

/** Parse organization API errors for the active UI language. */
export function parseOrganizationApiError(error, lang = "en") {
  return getStoreApiErrorMessage(error?.response?.data, lang, {
    generic: lang === "ar" ? "حدث خطأ غير متوقع" : "Something went wrong",
  })
}

export const FLOOR_DESIGN_LABEL = "Floor Plan to 2D"

export function matchesFloorDesignTitle(title) {
  const raw = String(title ?? "")
  const lower = raw.toLowerCase()
  return (
    lower.includes("floor plan") ||
    lower.includes("floor design") ||
    raw.includes("تصميم الأرضية")
  )
}

export function normalizeFeatureKey(feature) {
  return String(feature ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-/g, "_")
}

const FEATURE_LABELS_EN = {
  home_redesign: "Room Redesign",
  room_redesign: "Room Redesign",
  text_to_image: "Text To Image",
  object_removal: "Quick Edits",
  quick_edit: "Quick Edits",
  sketch_to_render: "Sketch To Image",
  furniture_finder: "Furniture Finder",
  furniture_design: "Furniture Design",
  design_transfer: "Design Transfer",
  pattern_transfer: "Pattern Transfer",
  decor_transfer: "Decor Transfer",
  furniture_removal: "Furniture Removal",
  zone_detection: "Zone Detection",
  video_generation: "Video Generation",
  reference_style: "Reference Style",
  wall_design: "Wall Design",
  floor_design: FLOOR_DESIGN_LABEL,
  floor_generation: "Floor Generation",
  enhance_image: "Enhance Image",
  furniture_replace: "Furniture Replace",
  rearrange_furniture: "Rearrange Furniture",
  expert_design: "Event Booth",
  room_planner: "Room Planner",
  ai_redecor: "AI Redecor",
  design_with_product: "Design With Product",
  product_visualizer: "Replicate Design",
}

const FEATURE_LABELS_AR = {
  home_redesign: "إعادة تصميم الغرفة",
  room_redesign: "إعادة تصميم الغرفة",
  text_to_image: "نص إلى صورة",
  object_removal: "تعديلات سريعة",
  quick_edit: "تعديلات سريعة",
  sketch_to_render: "رسم إلى صورة",
  furniture_finder: "مُستكشف الأثاث",
  furniture_design: "تصميم الأثاث",
  design_transfer: "نقل التصميم",
  pattern_transfer: "نقل النمط",
  decor_transfer: "نقل الديكور",
  furniture_removal: "إزالة الأثاث",
  zone_detection: "كشف المناطق",
  video_generation: "إنشاء فيديو",
  reference_style: "نمط المرجع",
  wall_design: "تصميم الجدار",
  floor_design: FLOOR_DESIGN_LABEL,
  floor_generation: "إنشاء الأرضية",
  enhance_image: "تحسين الصورة",
  furniture_replace: "استبدال الأثاث",
  rearrange_furniture: "إعادة ترتيب الأثاث",
  expert_design: "بوث الفعاليات",
  room_planner: "مخطط الغرفة",
  ai_redecor: "إعادة ديكور بالذكاء الاصطناعي",
  design_with_product: "التصميم بالمنتجات",
  product_visualizer: "استنساخ التصميم",
}

const FLOOR_GENERATION_IMAGE_LABELS_EN = {
  dollhouse_front: "Dollhouse Front",
  dollhouse_back: "Dollhouse Back",
  floor: "Floor",
  floor_back: "Floor Back",
  compose_primary: "Composition",
}

const FLOOR_GENERATION_IMAGE_LABELS_AR = {
  dollhouse_front: "واجهة منزل الدمية",
  dollhouse_back: "خلف منزل الدمية",
  floor: "الأرضية",
  floor_back: "خلف الأرضية",
  compose_primary: "التكوين",
}

/** Human-readable label for room-planner image keys (e.g. wall_north, floor_isometric). */
export function formatFloorGenerationImageKey(imageKey, lang = "en") {
  const key = String(imageKey ?? "").trim()
  if (!key) return lang === "ar" ? "صورة" : "Image"

  const fixed =
    lang === "ar"
      ? FLOOR_GENERATION_IMAGE_LABELS_AR
      : FLOOR_GENERATION_IMAGE_LABELS_EN
  if (fixed[key]) return fixed[key]

  if (key.startsWith("wall_")) {
    const part = key.slice(5).replace(/_/g, " ")
    return lang === "ar" ? `جدار ${part}` : `Wall ${part}`
  }

  if (key.startsWith("reused_")) {
    const suffix = key.endsWith("_back") ? " (back)" : ""
    const index = key.replace(/^reused_/, "").replace(/_back$/, "")
    return lang === "ar"
      ? `معاد استخدام ${index}${suffix ? " (خلف)" : ""}`
      : `Reused ${index}${suffix}`
  }

  if (key === "generate_primary" || key === "compose_primary") {
    return null
  }

  if (key.endsWith("_primary")) {
    const job = key.replace(/_primary$/, "").replace(/_/g, " ")
    return lang === "ar" ? `${job} رئيسي` : `${job} primary`
  }

  if (key.includes("_")) {
    const [jobKind, ...rest] = key.split("_")
    const viewName = rest.join(" ").replace(/_/g, " ")
    const jobLabel = jobKind.replace(/_/g, " ")
    return lang === "ar" ? `${jobLabel} ${viewName}` : `${jobLabel} ${viewName}`
  }

  return key.replace(/_/g, " ")
}

/** Indexed output label for user-detail room planner gallery. */
export function formatRoomPlannerOutputLabel(index, lang = "en") {
  const n = Number(index) + 1
  return lang === "ar" ? `صورة ${n}` : `Output image ${n}`
}

export function getRoomPlannerCacheKey(uuid, roomName = null) {
  const room = String(roomName ?? "")
    .trim()
    .toLowerCase()
  return room ? `${uuid}::${room}` : String(uuid)
}

export function roomPlannerNamesMatch(a, b) {
  if (!a || !b) return true
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase()
}

const ROOM_PLANNER_USER_DETAIL_PREVIEW_KEYS = [
  "dollhouse_front",
  "composition_front",
  "compose_primary",
  "compose_composition_front",
]

/** Unwrap nested ai_feature_detail / user_detail API envelopes. */
export function unwrapAiFeatureDetailPayload(payload) {
  if (!payload || typeof payload !== "object") return payload
  if (payload.result && typeof payload.result === "object") return payload.result
  if (payload.data?.result && typeof payload.data.result === "object") {
    return payload.data.result
  }
  if (payload.data && typeof payload.data === "object") return payload.data
  return payload
}

/** User detail shows only the dollhouse / composition front — not wall/floor angles. */
export function pickRoomPlannerPreviewImage(images) {
  if (!Array.isArray(images) || images.length === 0) return null

  for (const preferred of ROOM_PLANNER_USER_DETAIL_PREVIEW_KEYS) {
    const match = images.find((img) => {
      const key = String(img?.key ?? "").toLowerCase()
      return key === preferred || key.endsWith(`_${preferred}`)
    })
    if (match?.url) return match
  }

  const wide = images.find((img) => isWideRoomPlannerImageKey(img?.key))
  return wide ?? images[0] ?? null
}

/** Panoramic room-planner renders (dollhouse / compose) must not be cropped to a square. */
export function isWideRoomPlannerImageKey(imageKey) {
  const key = String(imageKey ?? "").trim().toLowerCase()
  return (
    key.startsWith("dollhouse_") ||
    key.startsWith("compose_") ||
    key.endsWith("_primary") ||
    key.includes("composition")
  )
}

/** Output images in user-detail modals — always contain, never crop. */
export function getFeatureModalOutputImageStyle(imageKey = null) {
  const wide = imageKey ? isWideRoomPlannerImageKey(imageKey) : false
  return {
    width: "100%",
    maxWidth: "100%",
    height: "auto",
    maxHeight: wide ? "min(50vh, 420px)" : "min(60vh, 480px)",
    objectFit: "contain",
    borderRadius: "8px",
    background: "#f4f4f4",
    display: "block",
  }
}

export function getRoomPlannerModalGridLayout(imageKey) {
  return {
    wide: isWideRoomPlannerImageKey(imageKey),
    imageStyle: getFeatureModalOutputImageStyle(imageKey),
  }
}

export function getRoomPlannerThumbnailLayout(imageKey) {
  const wide = isWideRoomPlannerImageKey(imageKey)
  return {
    wide,
    imageStyle: {
      width: "100%",
      minHeight: wide ? 150 : 160,
      maxHeight: wide ? 240 : 200,
      aspectRatio: wide ? "16 / 9" : "3 / 4",
      objectFit: "contain",
      borderRadius: "8px",
      background: "#f4f4f4",
      display: "block",
    },
  }
}

/** Full-size room planner preview in user-detail modal — never upscale past native resolution. */
export function getRoomPlannerModalImageStyle() {
  return {
    maxWidth: "100%",
    width: "auto",
    height: "auto",
    maxHeight: "min(70vh, 720px)",
    objectFit: "contain",
    borderRadius: "8px",
    display: "block",
  }
}

/** Room name from timeline detail, e.g. "... for Walk-In Closet". */
export function extractRoomNameFromTimelineDetail(detailText) {
  if (typeof detailText !== "string" || !detailText.trim()) return null
  const en = detailText.match(/for\s+(.+)$/i)
  if (en) return en[1].trim()
  const ar = detailText.match(/(?:لـ|ل\s+)(.+)$/)
  if (ar) return ar[1].trim()
  return null
}

/** Normalize ai_feature_detail / recent_generations payload for room-planner gallery. */
export function normalizeRoomPlannerDetail(detail, fallbackRoomName = null) {
  const raw = unwrapAiFeatureDetailPayload(detail) ?? {}

  const images = []
  const addImage = (key, url) => {
    const u = typeof url === "string" ? url.trim() : ""
    if (!u) return
    if (images.some((img) => img.url === u)) return
    images.push({ key: key || `image_${images.length}`, url: u })
  }

  const absorbPlannerItem = (item) => {
    if (!item || typeof item !== "object") return
    if (Array.isArray(item.images)) {
      for (const img of item.images) {
        if (typeof img === "string") addImage(null, img)
        else if (img && typeof img === "object") addImage(img.key, img.url)
      }
    }
    if (typeof item.output === "string" && item.output.trim()) {
      addImage("compose_primary", item.output.trim())
    }
    if (item.url) addImage(item.key, item.url)
    if (item.image_url) addImage(item.key, item.image_url)
    if (item.img_url) addImage(item.key || "output", item.img_url)
  }

  if (Array.isArray(raw.images)) {
    for (const item of raw.images) {
      if (typeof item === "string") addImage(null, item)
      else if (item && typeof item === "object") addImage(item.key, item.url)
    }
  }

  if (Array.isArray(raw.list) && raw.list.length > 0) {
    absorbPlannerItem(raw.list[0])
  } else {
    absorbPlannerItem(raw)
  }

  const firstListItem = Array.isArray(raw.list) ? raw.list[0] : raw

  const preferredPreview = pickRoomPlannerPreviewImage(images)
  const preview =
    preferredPreview?.url ||
    (typeof raw.preview === "string" && raw.preview.trim()) ||
    (typeof firstListItem?.output === "string" && firstListItem.output.trim()) ||
    images[0]?.url ||
    (typeof raw.output === "string" && raw.output.trim()) ||
    null

  const listDate = firstListItem?.date
  const listTime = firstListItem?.time

  return {
    render_id: raw.render_id ?? raw.uuid ?? raw.id ?? null,
    room_id: raw.room_id ?? null,
    room_name:
      raw.room_name ||
      firstListItem?.room_name ||
      fallbackRoomName ||
      null,
    status: raw.status ?? firstListItem?.status ?? null,
    preview,
    images,
    time_created:
      raw.time_created ??
      raw.date ??
      (listDate && listTime ? `${listDate}T${listTime}` : listDate) ??
      null,
    featureKey: "room_planner",
  }
}

const RECENT_GEN_URL_KEYS = [
  "preview",
  "img_url",
  "uploaded_img_url",
  "image",
  "output",
  "url",
  "output_url",
  "outputUrl",
  "image_url",
  "imageUrl",
  "thumbnail",
  "thumb",
  "result",
  "generated_image",
  "generatedImage",
  "file",
  "src",
  "photo",
  "picture",
  "input_image",
  "output_image",
]

const RECENT_GEN_URL_ARRAY_KEYS = [
  "img_urls",
  "outputs",
  "images",
  "urls",
  "results",
  "files",
]

function looksLikeRecentGenerationUrl(value) {
  if (typeof value !== "string" || !value.trim()) return false
  const t = value.trim()
  return (
    /^https?:\/\//i.test(t) ||
    t.startsWith("//") ||
    t.startsWith("/") ||
    t.startsWith("blob:") ||
    t.startsWith("data:image") ||
    t.includes(".amazonaws.com") ||
    t.includes("cloudfront.net")
  )
}

/** ISO string (or anything Date parses) → epoch ms, or null when unusable. */
function parseGenerationTimestamp(value) {
  if (value == null || value === "") return null
  const timestamp = new Date(value).getTime()
  return Number.isFinite(timestamp) ? timestamp : null
}

/**
 * Legacy ordering fallback: recover a time from the `_YYYYMMDD_HHMMSS_` stamp
 * older image paths carry. Superseded by recent_generation_times — delete this
 * once every deployed backend sends that field.
 */
function getRecentGenerationTimestampFromUrl(imageUrl) {
  if (typeof imageUrl !== "string" || !imageUrl) return 0
  const fileName = imageUrl.split("/").pop() || ""
  const match = fileName.match(/_(\d{8})_(\d{6})_/)
  if (!match) return 0

  const datePart = match[1]
  const timePart = match[2]
  const year = Number(datePart.slice(0, 4))
  const month = Number(datePart.slice(4, 6)) - 1
  const day = Number(datePart.slice(6, 8))
  const hour = Number(timePart.slice(0, 2))
  const minute = Number(timePart.slice(2, 4))
  const second = Number(timePart.slice(4, 6))

  return new Date(year, month, day, hour, minute, second).getTime()
}

/** Extract a thumbnail URL from recent_generations feature values (string or gallery object). */
export function extractRecentGenerationImageUrl(raw) {
  if (typeof raw === "string" && raw.trim()) return raw.trim()
  if (!raw || typeof raw !== "object") return null

  for (const key of RECENT_GEN_URL_KEYS) {
    const value = raw[key]
    if (typeof value !== "string" || !value.trim()) continue
    const trimmed = value.trim()
    if (
      looksLikeRecentGenerationUrl(trimmed) ||
      key === "img_url" ||
      key === "uploaded_img_url"
    ) {
      return trimmed
    }
  }

  for (const key of RECENT_GEN_URL_KEYS) {
    const value = raw[key]
    if (typeof value === "string" && value.trim()) return value.trim()
  }

  for (const arrayKey of RECENT_GEN_URL_ARRAY_KEYS) {
    const arr = raw[arrayKey]
    if (!Array.isArray(arr)) continue
    for (const item of arr) {
      if (typeof item === "string" && item.trim()) return item.trim()
      if (item && typeof item === "object") {
        const nested = extractRecentGenerationImageUrl(item)
        if (nested) return nested
      }
    }
  }

  return null
}

/**
 * Load full room-planner detail (all output images) for timeline / recent gens.
 * Tries room_planner, floor_generation, then no feature filter.
 */
export async function fetchRoomPlannerDetail(
  fetchDetail,
  uuid,
  fallbackRoomName = null
) {
  if (!uuid || typeof fetchDetail !== "function") return null

  const featureParams = ["room_planner", "floor_generation", null]
  let detailPayload = null
  let bestNormalized = null

  for (const feature of featureParams) {
    try {
      const res = await fetchDetail(uuid, feature)
      const candidate = unwrapAiFeatureDetailPayload(res?.data || res)
      if (!candidate || typeof candidate !== "object") continue

      detailPayload = candidate
      const normalized = normalizeRoomPlannerDetail(candidate, fallbackRoomName)
      if (normalized?.images?.length > 0 || normalized?.preview) {
        bestNormalized = normalized
        if (normalized.images?.length > 0) return normalized
      }
    } catch {
      // try next feature param
    }
  }

  if (bestNormalized) return bestNormalized
  return normalizeRoomPlannerDetail(detailPayload, fallbackRoomName)
}

const TIMELINE_STUB_FEATURE_KEYS = new Set([
  "reference_style",
  "wall_design",
  "floor_design",
  "design_with_product",
  "product_visualizer",
  "furniture_design",
])

/**
 * Parse GET /account/user_detail/ recent_generations into gallery rows.
 * Supports URL strings, img_url objects, and floor_generation room-planner payloads.
 *
 * `generationTimes` is the API's recent_generation_times map ({feature: ISO}) —
 * the authoritative sort key, taken from the generation record itself. A feature
 * whose newest render has no stored image comes back as null, so every read goes
 * through parseGenerationTimestamp rather than assuming a string.
 */
export function parseRecentGenerations(recentGenerations, generationTimes) {
  const source =
    recentGenerations?.generations &&
    typeof recentGenerations.generations === "object"
      ? recentGenerations.generations
      : recentGenerations

  if (!source || typeof source !== "object" || Array.isArray(source)) return []

  const timeMap =
    generationTimes && typeof generationTimes === "object" && !Array.isArray(generationTimes)
      ? generationTimes
      : recentGenerations?.times && typeof recentGenerations.times === "object"
      ? recentGenerations.times
      : {}

  const rows = []

  const pushRow = (featureKey, img, extras = {}) => {
    const hasFloorPlannerUuid =
      featureKey === "floor_generation" && Boolean(extras.uuid)
    if (!img && !hasFloorPlannerUuid) return
    const timestamp =
      extras.timestamp ??
      parseGenerationTimestamp(extras.time_created) ??
      parseGenerationTimestamp(timeMap[featureKey]) ??
      (img ? getRecentGenerationTimestampFromUrl(img) : 0)

    rows.push({
      key: featureKey,
      img: img || null,
      timestamp,
      floorGeneration: extras.floorGeneration ?? null,
      uuid: extras.uuid ?? null,
      roomName: extras.roomName ?? null,
      isTimelineStub: false,
      timelineItem: null,
    })
  }

  for (const [featureKey, featureValue] of Object.entries(source)) {
    if (featureValue == null) continue

    if (featureKey === "floor_generation") {
      if (typeof featureValue === "object") {
        const normalized = normalizeRoomPlannerDetail(featureValue)
        const uuid =
          normalized?.render_id ??
          featureValue.render_id ??
          featureValue.uuid ??
          featureValue.id ??
          null
        const preview = normalized?.preview ?? null
        if (preview || uuid) {
          pushRow(featureKey, preview, {
            floorGeneration: normalized,
            time_created: normalized?.time_created,
            roomName: normalized?.room_name,
            uuid,
          })
        }
      }
      continue
    }

    const addValue = (value) => {
      if (typeof value === "string") {
        pushRow(featureKey, value.trim())
        return
      }
      if (value && typeof value === "object") {
        const img = extractRecentGenerationImageUrl(value)
        pushRow(featureKey, img, {
          time_created: value.time_created,
          uuid: value.uuid ?? value.id ?? null,
        })
      }
    }

    if (Array.isArray(featureValue)) {
      featureValue.forEach(addValue)
    } else {
      addValue(featureValue)
    }
  }

  return rows.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
}

/** Fallback rows from timeline when recent_generations has no thumbnail for new features. */
export function buildRecentGenerationStubsFromTimeline(
  timelineItems,
  lang = "en"
) {
  if (!Array.isArray(timelineItems)) return []
  const stubs = []
  let latestRoomPlanner = null

  for (const item of timelineItems) {
    if (!item?.uuid) continue

    if (item.type === "room_planner") {
      const detailText = lang === "ar" ? item.detail_arabic : item.detail
      const stub = {
        key: "floor_generation",
        img: null,
        uuid: item.uuid,
        timelineItem: item,
        timestamp: item.date ? new Date(item.date).getTime() : 0,
        floorGeneration: null,
        roomName: extractRoomNameFromTimelineDetail(detailText),
        isTimelineStub: true,
      }
      // Timeline is newest-first — keep the first room_planner row only.
      if (!latestRoomPlanner) {
        latestRoomPlanner = stub
      }
      continue
    }

    if (item.type !== "generation") continue
    const featureKey = resolveTimelineFeatureKey(item)
    if (!featureKey || !TIMELINE_STUB_FEATURE_KEYS.has(featureKey)) continue

    stubs.push({
      key: featureKey,
      img: null,
      uuid: item.uuid,
      timelineItem: item,
      timestamp: item.date ? new Date(item.date).getTime() : 0,
      floorGeneration: null,
      roomName: null,
      isTimelineStub: true,
    })
  }

  if (latestRoomPlanner) {
    stubs.push(latestRoomPlanner)
  }

  return stubs.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
}

/**
 * Legacy ordering fallback for backends that predate recent_generation_times:
 * borrow each feature's last timeline date for rows left without a timestamp.
 * Date-only and bounded by the timeline's range, so it is a last resort — drop
 * it, and getRecentGenerationTimestampFromUrl, once that field is everywhere.
 */
export function applyTimelineTimestampsToRecentGenerations(rows, timelineItems) {
  if (!Array.isArray(rows) || rows.length === 0) return rows || []
  if (!Array.isArray(timelineItems) || timelineItems.length === 0) return rows

  const latestByFeature = new Map()
  for (const item of timelineItems) {
    const key = resolveTimelineFeatureKey(item)
    if (!key || !item?.date) continue
    const timestamp = new Date(item.date).getTime()
    if (!Number.isFinite(timestamp)) continue
    if ((latestByFeature.get(key) || 0) < timestamp) {
      latestByFeature.set(key, timestamp)
    }
  }
  if (latestByFeature.size === 0) return rows

  return rows
    .map((row) =>
      row.timestamp
        ? row
        : { ...row, timestamp: latestByFeature.get(row.key) || 0 }
    )
    .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
}

/** Merge API recent_generations with timeline stubs (timeline wins for latest room planner). */
export function mergeRecentGenerations(apiRows, timelineStubs) {
  const apiKeys = new Set(apiRows.map((row) => row.key))
  const apiFloor = apiRows.find((row) => row.key === "floor_generation")
  const timelineFloor = timelineStubs.find((row) => row.key === "floor_generation")

  // Prefer the newest timeline room_planner event over stale API floor_generation.
  let chosenFloor = null
  if (timelineFloor && apiFloor) {
    const timelineTs = timelineFloor.timestamp || 0
    const apiTs = apiFloor.timestamp || 0
    chosenFloor = timelineTs >= apiTs ? timelineFloor : apiFloor
  } else {
    chosenFloor = timelineFloor ?? apiFloor ?? null
  }

  const merged = apiRows.filter((row) => row.key !== "floor_generation")
  const stubKeysAdded = new Set()

  for (const stub of timelineStubs) {
    if (stub.key === "floor_generation") continue
    if (apiKeys.has(stub.key)) continue
    if (stubKeysAdded.has(stub.key)) continue
    stubKeysAdded.add(stub.key)
    merged.push(stub)
  }

  if (chosenFloor) merged.push(chosenFloor)

  return merged.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
}

/** Map a timeline_list row title to a normalized feature key. */
export function resolveTimelineFeatureKey(item) {
  const explicit = normalizeFeatureKey(
    item?.featureKey ?? item?.feature ?? item?.feature_key
  )
  if (explicit) return explicit

  const title = `${item?.title || ""} ${item?.title_arabic || ""}`.toLowerCase()
  if (
    title.includes("furniture design") ||
    title.includes("added furniture to environment") ||
    title.includes("إضافة الأثاث إلى المساحة") ||
    title.includes("إضافة الاثاث إلى المساحة") ||
    title.includes("تصميم الأثاث") ||
    title.includes("تصميم الاثاث")
  ) {
    return "furniture_design"
  }
  if (title.includes("furniture finder")) return "furniture_finder"
  if (title.includes("sketch")) return "sketch_to_render"
  if (title.includes("text to image")) return "text_to_image"
  if (title.includes("quick edit")) return "quick_edit"
  if (title.includes("furniture removal") || title.includes("virtual unstaging")) {
    return "furniture_removal"
  }
  if (title.includes("enhance image")) return "enhance_image"
  if (
    title.includes("expert design") ||
    title.includes("event booth") ||
    title.includes("expert_design") ||
    title.includes("بوث الفعاليات") ||
    title.includes("تصميم الخبير")
  ) {
    return "expert_design"
  }
  if (
    title.includes("furniture replace") ||
    title.includes("استبدال الأثاث") ||
    title.includes("استبدال الاثاث")
  ) {
    return "furniture_replace"
  }
  if (
    title.includes("rearrange furniture") ||
    title.includes("إعادة ترتيب الأثاث") ||
    title.includes("إعادة ترتيب الاثاث")
  ) {
    return "rearrange_furniture"
  }
  if (title.includes("reference style") || title.includes("نمط المرجع")) {
    return "reference_style"
  }
  if (
    title.includes("design with product") ||
    title.includes("التصميم بالمنتجات")
  ) {
    return "design_with_product"
  }
  if (
    title.includes("product visualizer") ||
    title.includes("replicate design") ||
    title.includes("استنساخ التصميم")
  ) {
    return "product_visualizer"
  }
  if (title.includes("wall design") || title.includes("تصميم الجدار")) {
    return "wall_design"
  }
  if (matchesFloorDesignTitle(title)) {
    return "floor_design"
  }
  if (title.includes("redesign") || title.includes("إعادة تصميم")) {
    return "room_redesign"
  }
  return null
}

export function formatFeatureLabel(feature, lang = "en") {
  const key = normalizeFeatureKey(feature)
  if (!key) return ""
  const known = getFeatureLabelIfKnown(key, lang)
  if (known) return known
  return key
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
}

export function getFeatureLabelIfKnown(feature, lang = "en") {
  const key = normalizeFeatureKey(feature)
  if (!key) return null
  const map = lang === "ar" ? FEATURE_LABELS_AR : FEATURE_LABELS_EN
  return map[key] ?? null
}

/** Dashboard SVG paths for feature keys (shared by usage chart, timeline, modals). */
const FEATURE_DASHBOARD_ICONS = {
  ai_generated: "/dashboard/ai-generated-icon.svg",
  furniture_finder: "/dashboard/sofa-icon.svg",
  furniture_design: "/dashboard/furniture-design-icon.svg",
  home_redesign: "/dashboard/room-redesign-icon.svg",
  room_redesign: "/dashboard/room-redesign-icon.svg",
  object_removal: "/dashboard/object-removal-icon.svg",
  quick_edit: "/dashboard/object-removal-icon.svg",
  sketch_to_render: "/dashboard/quick-edits.svg",
  text_to_image: "/dashboard/credits-icon.svg",
  furniture_removal: "/dashboard/virtua_unstaging.svg",
  furniture_replace: "/dashboard/replace-furniture.svg",
  rearrange_furniture: "/dashboard/rearrange-furniture.svg",
  enhance_image: "/dashboard/image-upload.svg",
  expert_design: "/dashboard/event-booth-icon.svg",
  reference_style: "/dashboard/reference-style-icon.svg",
  wall_design: "/dashboard/wall-design-icon.svg",
  floor_design: "/dashboard/floor-design-icon.svg",
  floor_generation: "/dashboard/floor-generation-icon.svg",
  design_with_product: "/dashboard/design-with-product-icon.svg",
  product_visualizer: "/dashboard/replicate-design-icon.svg",
}

export function getFeatureDashboardIcon(
  featureKey,
  fallback = "/dashboard/default-icon.svg"
) {
  const key = normalizeFeatureKey(featureKey)
  if (!key) return fallback
  return FEATURE_DASHBOARD_ICONS[key] ?? fallback
}

const GENERATION_PRIMARY_INPUT_KEYS = [
  "input",
  "image_url",
  "uploaded_img_url",
  "img_url",
  "input_image",
]

function pushGenerationInputImage(inputs, seen, url, key, labelEn, labelAr) {
  const trimmed = typeof url === "string" ? url.trim() : ""
  if (!trimmed || seen.has(trimmed)) return
  seen.add(trimmed)
  inputs.push({ key, url: trimmed, labelEn, labelAr })
}

/**
 * Input images for ai_feature_detail rows (timeline / recent generations modals).
 */
export function collectGenerationInputImages(
  gen,
  featureKey = "",
  title = ""
) {
  if (!gen || typeof gen !== "object") return []

  let key = normalizeFeatureKey(featureKey)
  if (!key && title) {
    key = resolveTimelineFeatureKey({ title }) || ""
  }
  const inputs = []
  const seen = new Set()

  const pushPrimaryInputs = (labelEn = "Input", labelAr = "المدخل") => {
    for (const field of GENERATION_PRIMARY_INPUT_KEYS) {
      pushGenerationInputImage(
        inputs,
        seen,
        gen[field],
        field,
        labelEn,
        labelAr
      )
    }
  }

  if (key === "reference_style") {
    pushGenerationInputImage(
      inputs,
      seen,
      gen.input || gen.image_url,
      "input",
      "Base image",
      "صورة أساسية"
    )
    pushGenerationInputImage(
      inputs,
      seen,
      gen.reference_image || gen.ref_url,
      "reference_image",
      "Reference image",
      "صورة مرجعية"
    )
    if (!inputs.length) pushPrimaryInputs()
    return inputs
  }

  if (key === "design_with_product") {
    pushGenerationInputImage(
      inputs,
      seen,
      gen.input || gen.image_url,
      "input",
      "Room photo",
      "صورة الغرفة"
    )
    pushGenerationInputImage(
      inputs,
      seen,
      gen.reference_image || gen.ref_url,
      "reference_image",
      "Product photo",
      "صورة المنتج"
    )
    if (!inputs.length) pushPrimaryInputs()
    return inputs
  }

  if (key === "product_visualizer") {
    pushGenerationInputImage(
      inputs,
      seen,
      gen.input || gen.image_url,
      "input",
      "Reference image",
      "صورة مرجعية"
    )
    pushGenerationInputImage(
      inputs,
      seen,
      gen.reference_image || gen.ref_url,
      "reference_image",
      "Room photo",
      "صورة الغرفة"
    )
    if (!inputs.length) pushPrimaryInputs()
    return inputs
  }

  // Furniture Design room step: the input is the piece render from step one.
  if (key === "furniture_design") {
    pushGenerationInputImage(
      inputs,
      seen,
      gen.input || gen.furniture_image,
      "input",
      "Furniture piece",
      "قطعة الأثاث"
    )
    if (!inputs.length) pushPrimaryInputs()
    return inputs
  }

  if (key === "wall_design" || key === "floor_design") {
    pushPrimaryInputs()
    return inputs
  }

  pushPrimaryInputs()
  return inputs
}

export function hasOrganizationFurnitureFinderPool(overview) {
  const sub = overview?.subscription
  if (!sub) return false

  const total =
    Number(sub.total_furniture_finder) || Number(sub.plan_furniture_finder) || 0
  if (total > 0) return true

  return getAllowedPlanFeaturesFromOverview(overview).includes("furniture_finder")
}

export function getAllowedPlanFeaturesFromOverview(overview) {
  const raw = overview?.organization?.allowed_plan_features
  if (!Array.isArray(raw)) return []

  const seen = new Set()
  const out = []
  for (const item of raw) {
    const key = normalizeFeatureKey(item)
    if (!key || seen.has(key)) continue
    seen.add(key)
    out.push(key)
  }
  return out
}

export function getOrganizationMembersSummaryFromOverview(overview) {
  const summary = overview?.members_summary
  if (!summary) return { total: 0, active: 0, invited: 0 }
  return {
    total: Number(summary.total) || 0,
    active: Number(summary.active) || 0,
    invited: Number(summary.invited) || 0,
  }
}

const ORG_FEATURE_SELECT_EXCLUDED = new Set(["furniture_finder"])

export function buildPlanFeatureOptions(allowedPlanFeatures, lang = "en") {
  const seen = new Set()
  const options = []
  for (const item of allowedPlanFeatures || []) {
    const value = normalizeFeatureKey(item)
    if (!value || seen.has(value) || ORG_FEATURE_SELECT_EXCLUDED.has(value)) continue
    seen.add(value)
    options.push({ label: formatFeatureLabel(value, lang), value })
  }
  return options
}

function parseDrfPaginatedResponse(response) {
  const data = response?.data
  const results = Array.isArray(data?.results) ? data.results : []
  return {
    count: Number(data?.count) || results.length,
    results,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
  }
}

export function parseOrganizationGenerationsResponse(response) {
  return parseDrfPaginatedResponse(response)
}

export function parseOrganizationMembersResponse(response) {
  return parseDrfPaginatedResponse(response).results
}

const asUrl = (value) =>
  typeof value === "string" && value.trim() ? value.trim() : null

export function getOrganizationGenerationOutputUrls(gen) {
  if (!gen || typeof gen !== "object") return []

  const urls = []
  const add = (value) => {
    const url = asUrl(value)
    if (url && !urls.includes(url)) urls.push(url)
  }

  if (Array.isArray(gen.img_urls)) {
    for (const item of gen.img_urls) add(item)
  }
  if (urls.length === 0) add(gen.img_url)

  return urls
}

export function getOrganizationGenerationMedia(gen) {
  if (!gen || typeof gen !== "object") {
    return { inputUrl: null, outputUrl: null, outputUrls: [], prompt: "" }
  }

  const outputUrls = getOrganizationGenerationOutputUrls(gen)

  return {
    inputUrl: asUrl(gen.input_image),
    outputUrl: outputUrls[0] ?? null,
    outputUrls,
    prompt: typeof gen.prompt === "string" ? gen.prompt.trim() : "",
  }
}

export function formatOrganizationMemberQuota(value) {
  if (value == null || value === "") return "—"
  const n = Number(value)
  return Number.isFinite(n) ? n : "—"
}

/** Furniture Finder fields returned by GET /account/organization/members/ */
export function getOrganizationMemberFurnitureFinderStats(member) {
  const remaining = Number(member?.remaining_furniture_finder)
  const pending = Number(member?.pending_allocation_furniture_finder)

  return {
    remaining: Number.isFinite(remaining) ? remaining : null,
    pending: Number.isFinite(pending) ? pending : 0,
  }
}

export function filterMemberFeaturesToPlan(memberFeatures, planFeatures) {
  const planSet = new Set(
    (planFeatures || []).map(normalizeFeatureKey).filter(Boolean),
  )
  if (!Array.isArray(memberFeatures)) return []

  return memberFeatures
    .map(normalizeFeatureKey)
    .filter((key) => planSet.has(key) && !ORG_FEATURE_SELECT_EXCLUDED.has(key))
}
